import { DEFAULT_TEMPLATE_FOR_PRODUCT } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { IconSearch, IconX } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link, redirect } from "@tanstack/react-router"
import clsx from "clsx"
import { useCallback, useMemo, useState } from "react"
import { EmptyState } from "../../components/EmptyState"
import { CardGridSkeleton } from "../../components/PageSkeletons"
import { VoxPageTitle } from "../../components/VoxPageTitle"
import { VoxPillButton } from "../../components/VoxPillButton"
import { VoxStatusPill } from "../../components/VoxStatusPill"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import { createSeries, deleteSeries, fetchSeries } from "../../lib/api"
import { formatDateTime } from "../../lib/format-date"
import { queryKeys } from "../../lib/query-keys"
import { templateOptions } from "../../lib/template-labels"
import { detectProduct, useProduct } from "../../lib/tenant"
import type { ISeries } from "../../lib/types/template"
import styles from "./campaigns.module.css"

interface ICreateSeriesFormValues {
  name: string
  description: string
  templateId: string
}

// Template badge colours — semantic Mantine palettes (no literal hue names):
// the D&D template wears the brand accent, the meeting template the neutral-
// positive `info` tone. See .claude/rules/theming.md (rules 3 + 6).
const TEMPLATE_COLORS: Record<string, string> = {
  "dnd-companion": "brand",
  "meeting-intelligence": "info",
}

/**
 * Map a templateId (or campaign metadata.system override) to the human
 * label shown on the Voxcanon system trust-chip. Free-form strings in
 * metadata pass through verbatim — uppercased — so a DM can drop in
 * "Pathfinder 2e" or "Cthulhu" via campaign settings without us needing
 * a new enum.
 */
function resolveSystemLabel(series: ISeries): string {
  const meta = series.metadata ?? {}
  const explicit = typeof meta.system === "string" ? meta.system.trim() : ""
  if (explicit) return explicit.toUpperCase()
  if (series.templateId === DEFAULT_TEMPLATE_FOR_PRODUCT.voxcanon) return "D&D 5E"
  if (series.templateId === DEFAULT_TEMPLATE_FOR_PRODUCT.lexicanon) return "MEETING"
  return "TTRPG"
}

function resolveImageUrl(series: ISeries): string | null {
  const meta = series.metadata ?? {}
  if (typeof meta.imageUrl === "string" && meta.imageUrl) return meta.imageUrl
  if (typeof meta.image_url === "string" && meta.image_url) return meta.image_url
  return null
}

/**
 * Status derivation — series rows don't carry a lifecycle column yet,
 * so we surface a single tone ("active") in the design until that
 * lands. Returning the tone separately keeps the card markup ready for
 * the future On-Hiatus / Concluded distinctions.
 */
type TCampaignStatus = "active" | "hiatus" | "concluded"

function deriveStatus(_series: ISeries): TCampaignStatus {
  return "active"
}

function CampaignsPage(): React.JSX.Element {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const { i18n } = useLingui()
  const product = useProduct()
  const [createOpen, setCreateOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<TCampaignStatus | "all">("all")
  const templateChoices = useMemo(() => templateOptions(i18n), [i18n])

  const form = useForm<ICreateSeriesFormValues>({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      description: "",
      templateId: "",
    },
    validate: {
      name: (value) => (value.trim().length === 0 ? i18n._(msg`Campaign name is required`) : null),
    },
  })

  const { data: campaigns, isLoading } = useCompanyQuery<ISeries[]>({
    key: (org) => queryKeys.series(org),
    queryFn: (company) => fetchSeries(company),
  })

  const createMutation = useCompanyMutation({
    mutationFn: (company, values: ICreateSeriesFormValues) =>
      createSeries(company, {
        name: values.name,
        description: values.description || undefined,
        templateId: values.templateId || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.series(orgId) })
      notifications.show({
        title: i18n._(msg`Campaign created`),
        message: i18n._(msg`Your new campaign has been created.`),
        color: "success",
      })
      setCreateOpen(false)
      form.reset()
    },
    onError: (error: Error) => {
      notifications.show({
        title: i18n._(msg`Failed to create campaign`),
        message: error.message,
        color: "danger",
      })
    },
  })

  const deleteMutation = useCompanyMutation({
    mutationFn: (company, id: string) => deleteSeries(company, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.series(orgId) })
      notifications.show({
        title: i18n._(msg`Campaign deleted`),
        message: i18n._(msg`The campaign has been removed.`),
        color: "warning",
      })
    },
    onError: (error: Error) => {
      notifications.show({
        title: i18n._(msg`Failed to delete campaign`),
        message: error.message,
        color: "danger",
      })
    },
  })

  const handleDeleteClick = useCallback(
    (campaign: ISeries, event: React.MouseEvent): void => {
      event.preventDefault()
      event.stopPropagation()
      modals.openConfirmModal({
        title: i18n._(msg`Delete campaign`),
        children: (
          <Text size="sm">
            <Trans>
              Are you sure you want to delete <strong>{campaign.name}</strong>? This action cannot be undone.
            </Trans>
          </Text>
        ),
        labels: { confirm: i18n._(msg`Delete`), cancel: i18n._(msg`Cancel`) },
        confirmProps: { color: "danger" },
        onConfirm: () => deleteMutation.mutate(campaign.id),
      })
    },
    [deleteMutation, i18n],
  )

  const handleCreateSubmit = useCallback(
    (values: ICreateSeriesFormValues): void => {
      createMutation.mutate(values)
    },
    [createMutation],
  )

  const handleCreateClose = useCallback((): void => {
    setCreateOpen(false)
    form.reset()
  }, [form])

  const filtered = useMemo(() => {
    const list = campaigns ?? []
    const q = search.trim().toLowerCase()
    return list.filter((c) => {
      if (statusFilter !== "all" && deriveStatus(c) !== statusFilter) return false
      if (!q) return true
      const haystacks = [c.name, c.description ?? "", resolveSystemLabel(c)].map((s) => s.toLowerCase())
      return haystacks.some((h) => h.includes(q))
    })
  }, [campaigns, search, statusFilter])

  const statusCounts = useMemo(() => {
    const list = campaigns ?? []
    return {
      all: list.length,
      active: list.filter((c) => deriveStatus(c) === "active").length,
      hiatus: list.filter((c) => deriveStatus(c) === "hiatus").length,
      concluded: list.filter((c) => deriveStatus(c) === "concluded").length,
    }
  }, [campaigns])

  // Shared modal — same form for both products.
  const createModal = (
    <Modal opened={createOpen} onClose={handleCreateClose} title={i18n._(msg`New campaign`)}>
      <form onSubmit={form.onSubmit(handleCreateSubmit)}>
        <Stack>
          <TextInput
            label={i18n._(msg`Name`)}
            placeholder={i18n._(msg`My TTRPG Campaign`)}
            required
            key={form.key("name")}
            {...form.getInputProps("name")}
          />
          <Textarea
            label={i18n._(msg`Description`)}
            placeholder={i18n._(msg`What is this campaign about?`)}
            rows={3}
            key={form.key("description")}
            {...form.getInputProps("description")}
          />
          <Select
            label={i18n._(msg`Template`)}
            placeholder={i18n._(msg`Choose a template (optional)`)}
            data={templateChoices}
            clearable
            key={form.key("templateId")}
            {...form.getInputProps("templateId")}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleCreateClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit" loading={createMutation.isPending}>
              <Trans>Create</Trans>
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )

  // ─────────────────────────────────────────────────────────────
  // Voxcanon path — cinematic page with full-bleed campaign cards
  // ─────────────────────────────────────────────────────────────
  if (product === "voxcanon") {
    const filterPills: { id: TCampaignStatus | "all"; labelKey: ReturnType<typeof msg>; count: number }[] = [
      { id: "all", labelKey: msg`All`, count: statusCounts.all },
      { id: "active", labelKey: msg`Active`, count: statusCounts.active },
      { id: "hiatus", labelKey: msg`On hiatus`, count: statusCounts.hiatus },
      { id: "concluded", labelKey: msg`Concluded`, count: statusCounts.concluded },
    ]

    return (
      <div className={styles.voxPage}>
        {createModal}
        <VoxPageTitle
          eyebrow={{ primary: i18n._(msg`Workspace`) }}
          leading={<Trans>Your</Trans>}
          accent={<Trans>Campaigns</Trans>}
          subline={
            campaigns && campaigns.length > 0 ? (
              <Trans>
                {campaigns.length} chronicles in progress. Click any to dive into characters, sessions, NPCs and the
                threads of story.
              </Trans>
            ) : (
              <Trans>Begin your first chronicle and the world will start writing itself.</Trans>
            )
          }
        />

        <div className={styles.voxToolbar}>
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder={i18n._(msg`Search campaigns…`)}
            aria-label={i18n._(msg`Search campaigns`)}
            leftSection={<IconSearch size={14} />}
            size="sm"
          />
          <VoxPillButton variant="record" leading="+" onClick={() => setCreateOpen(true)}>
            <Trans>New campaign</Trans>
          </VoxPillButton>
        </div>

        <div className={styles.voxFilterRow}>
          {filterPills
            .filter((p) => p.id === "all" || p.count > 0)
            .map((p) => {
              const isActive = statusFilter === p.id
              return (
                <button
                  key={p.id}
                  type="button"
                  className={clsx(styles.voxFilterPill, isActive && styles.voxFilterPillActive)}
                  onClick={() => setStatusFilter(p.id)}
                  aria-pressed={isActive}
                >
                  <span>{i18n._(p.labelKey)}</span>
                  <span className={styles.voxFilterCount}>{p.count}</span>
                </button>
              )
            })}
        </div>

        {isLoading && (
          <div className={styles.voxGrid}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={String(i)} height={300} radius="md" />
            ))}
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className={styles.voxEmptyState}>
            <h3 className={styles.voxEmptyTitle}>
              <Trans>The chronicle awaits</Trans>
            </h3>
            <p className={styles.voxEmptyBody}>
              {search.trim() || statusFilter !== "all" ? (
                <Trans>No campaigns match these filters yet.</Trans>
              ) : (
                <Trans>Create your first campaign to start managing characters, sessions, and stories.</Trans>
              )}
            </p>
            {!search.trim() && statusFilter === "all" && (
              <VoxPillButton variant="arcane" leading="+" onClick={() => setCreateOpen(true)}>
                <Trans>Begin a new chronicle</Trans>
              </VoxPillButton>
            )}
          </div>
        )}

        {!isLoading && filtered.length > 0 && (
          <div className={styles.voxGrid}>
            {filtered.map((campaign) => {
              const imageUrl = resolveImageUrl(campaign)
              const system = resolveSystemLabel(campaign)
              const status = deriveStatus(campaign)
              const initial = campaign.name.charAt(0).toUpperCase()
              return (
                <Link
                  key={campaign.id}
                  to="/campaigns/$id"
                  params={{ id: campaign.id }}
                  className={styles.voxCard}
                  data-testid="campaign-card"
                >
                  {imageUrl ? (
                    <div
                      className={styles.voxCardImage}
                      style={{ backgroundImage: `url(${JSON.stringify(imageUrl).slice(1, -1)})` }}
                      aria-hidden="true"
                    />
                  ) : (
                    <div className={styles.voxCardImageEmpty} aria-hidden="true">
                      {initial}
                    </div>
                  )}

                  <div className={styles.voxCardOverlay}>
                    <span className={styles.voxSystemChip}>{system}</span>
                    {status === "active" && (
                      <VoxStatusPill tone="active">
                        <Trans>Active</Trans>
                      </VoxStatusPill>
                    )}
                    {status === "hiatus" && (
                      <VoxStatusPill tone="archived">
                        <Trans>On hiatus</Trans>
                      </VoxStatusPill>
                    )}
                    {status === "concluded" && (
                      <VoxStatusPill tone="revealed">
                        <Trans>Concluded</Trans>
                      </VoxStatusPill>
                    )}
                  </div>

                  <ActionIcon
                    variant="filled"
                    color="dark"
                    size="sm"
                    radius="xl"
                    className={styles.voxCardDelete}
                    aria-label={i18n._(msg`Delete campaign`)}
                    loading={deleteMutation.isPending}
                    onClick={(e) => handleDeleteClick(campaign, e)}
                  >
                    <IconX size={12} />
                  </ActionIcon>

                  <div className={styles.voxCardBody}>
                    <h3 className={styles.voxCardTitle}>{campaign.name}</h3>
                    {campaign.description && <div className={styles.voxCardDescription}>{campaign.description}</div>}
                    <div className={styles.voxCardMeta}>
                      <span>{campaign.createdAt ? formatDateTime(i18n, campaign.createdAt) : "—"}</span>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────
  // Lexicanon path — existing Mantine card grid (unchanged)
  // ─────────────────────────────────────────────────────────────
  return (
    <Container size="lg" className={styles.page}>
      <Group justify="space-between" className={styles.header}>
        <Stack gap={4}>
          <Title order={2}>
            <Trans>Campaigns</Trans>
          </Title>
          <Text size="sm" c="dimmed">
            <Trans>Manage your TTRPG campaigns and character rosters.</Trans>
          </Text>
        </Stack>
        <Button onClick={() => setCreateOpen(true)}>
          <Trans>New campaign</Trans>
        </Button>
      </Group>

      {isLoading && <CardGridSkeleton count={3} />}

      {!isLoading && campaigns?.length === 0 && (
        <EmptyState
          icon={<Text fz={28}>⚔️</Text>}
          title={<Trans>No campaigns yet</Trans>}
          description={<Trans>Create your first campaign to start managing characters, sessions, and stories.</Trans>}
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Trans>Create your first campaign</Trans>
            </Button>
          }
        />
      )}

      {!isLoading && campaigns && campaigns.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {campaigns.map((campaign) => (
            <Card
              key={campaign.id}
              className={styles.card}
              withBorder
              renderRoot={(props) => <Link to="/campaigns/$id" params={{ id: campaign.id }} {...props} />}
            >
              <Stack className={styles.cardContent}>
                <Group justify="space-between" wrap="nowrap">
                  <Text className={styles.cardTitle} lineClamp={1}>
                    {campaign.name}
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    color="danger"
                    size="sm"
                    aria-label={i18n._(msg`Delete campaign`)}
                    loading={deleteMutation.isPending}
                    onClick={(e) => handleDeleteClick(campaign, e)}
                  >
                    ✕
                  </ActionIcon>
                </Group>

                {campaign.templateId && (
                  <Badge
                    color={TEMPLATE_COLORS[campaign.templateId] ?? "gray"}
                    variant="light"
                    size="sm"
                    w="fit-content"
                  >
                    {campaign.templateId}
                  </Badge>
                )}

                <Text className={styles.cardDescription} lineClamp={2}>
                  {campaign.description ?? i18n._(msg`No description`)}
                </Text>

                <div className={styles.cardMeta}>
                  <Text size="xs" c="dimmed">
                    {campaign.createdAt ? formatDateTime(i18n, campaign.createdAt) : "—"}
                  </Text>
                </div>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {createModal}
    </Container>
  )
}

export const Route = createFileRoute("/_auth/campaigns")({
  component: CampaignsPage,
  beforeLoad: () => {
    // Campaigns is a Voxcanon-only concept (TTRPG chronicles). Lexicanon
    // bounces to the dashboard so direct URL hits don't show out-of-product
    // copy. Use the canonical detector — it honours VITE_PRODUCT first, so the
    // Tauri build (which has no brand hostname) resolves correctly too.
    if (detectProduct() === "voxcanon") return
    throw redirect({ to: "/" })
  },
})
